import { useState } from 'react';
import { CATEGORY_META, NOTIFICATION_SEVERITIES, type NotificationSeverity, type ProjectDTO } from '@devrobot/shared';
import { CircleCheck, CircleX, MessageSquare, Plus, Send, Trash2, Webhook } from 'lucide-react';
import {
  useCreateIntegration,
  useDeleteIntegration,
  useProjectIntegrations,
  useRotateIntegrationSecret,
  useTestIntegration,
  useUpdateIntegration,
  type IntegrationDTO,
} from '../../api/notifications';
import { CopyButton } from '../../components/CopyButton';
import { EmptyState } from '../../components/EmptyState';
import { ErrorAlert } from '../../components/ErrorAlert';
import { RelativeTime } from '../../components/RelativeTime';
import { SkeletonLines } from '../../components/Skeleton';
import { useToast } from '../../components/Toaster';

const SEVERITY_LABEL: Record<NotificationSeverity, string> = {
  info: 'Everything',
  warning: 'Warnings and problems',
  critical: 'Only critical',
};

/**
 * Where a project sends its notifications outside DevRobot.
 *
 * Only people who can manage the project's environments see this working: the server gates
 * it on the same capability, so a read-only visitor gets a plain error rather than a form
 * that fails on submit.
 */
export function IntegrationsTab({ project }: { project: ProjectDTO }) {
  const query = useProjectIntegrations(project.key);
  const create = useCreateIntegration(project.key);
  const [adding, setAdding] = useState(false);
  const [secret, setSecret] = useState<string>();
  const toast = useToast();

  if (query.isPending) return <SkeletonLines lines={5} />;
  if (query.error) return <ErrorAlert error={query.error} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-prose text-sm text-base-content/60">
          Send this project&rsquo;s notifications to your own tooling or a team channel. These fire for the whole project, whatever any one person&rsquo;s settings say.
        </p>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdding((v) => !v)}>
          <Plus className="size-4" aria-hidden="true" />
          Add
        </button>
      </div>

      {secret ? (
        <div role="alert" className="alert alert-warning alert-soft">
          <span className="min-w-0">
            <span className="block font-medium">Copy the signing secret now</span>
            <span className="block text-sm">It is shown once and never again. Use it to verify the signature on every delivery.</span>
            <code className="mt-2 block truncate font-mono text-sm">{secret}</code>
          </span>
          <span className="flex gap-2">
            <CopyButton value={secret} />
            <button type="button" className="btn btn-sm" onClick={() => setSecret(undefined)}>
              Done
            </button>
          </span>
        </div>
      ) : null}

      {adding ? (
        <AddForm
          busy={create.isPending}
          error={create.error}
          onCancel={() => setAdding(false)}
          onSubmit={(vars) =>
            create.mutate(vars, {
              onSuccess: (result) => {
                setAdding(false);
                if (result.secret) setSecret(result.secret);
                toast.success('Integration added');
              },
            })
          }
        />
      ) : null}

      {query.data.length === 0 && !adding ? (
        <EmptyState icon={Webhook} title="No integrations yet">
          Add a signed webhook to reach your own tooling, or a Rocket.Chat channel for the team.
        </EmptyState>
      ) : (
        <ul className="list card dr-surface">
          {query.data.map((i) => (
            <Row key={i.id} projectKey={project.key} integration={i} onSecret={setSecret} />
          ))}
        </ul>
      )}
    </div>
  );
}

function AddForm({
  busy,
  error,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (vars: { kind: 'webhook' | 'rocketchat'; url: string; categories?: string[]; minSeverity?: NotificationSeverity }) => void;
}) {
  const [kind, setKind] = useState<'webhook' | 'rocketchat'>('webhook');
  const [url, setUrl] = useState('');
  const [minSeverity, setMinSeverity] = useState<NotificationSeverity>('warning');

  return (
    <form
      className="card dr-surface"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ kind, url: url.trim(), minSeverity });
      }}
    >
      <div className="card-body gap-4 p-5 sm:p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Kind</legend>
            <select className="select w-full" value={kind} onChange={(e) => setKind(e.target.value as 'webhook' | 'rocketchat')}>
              <option value="webhook">Signed webhook</option>
              <option value="rocketchat">Rocket.Chat channel</option>
            </select>
            <p className="label">{kind === 'webhook' ? 'JSON with an HMAC signature header.' : 'A Rocket.Chat incoming webhook URL.'}</p>
          </fieldset>
          <fieldset className="fieldset">
            <legend className="fieldset-legend">How much to send</legend>
            <select className="select w-full" value={minSeverity} onChange={(e) => setMinSeverity(e.target.value as NotificationSeverity)}>
              {NOTIFICATION_SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {SEVERITY_LABEL[s]}
                </option>
              ))}
            </select>
          </fieldset>
        </div>
        <fieldset className="fieldset">
          <legend className="fieldset-legend">URL</legend>
          <input className="input w-full" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/hooks/devrobot" required />
          <p className="label">The full URL is stored encrypted and never shown again.</p>
        </fieldset>
        {error ? <ErrorAlert error={error} /> : null}
        <div className="card-actions justify-end">
          <button type="button" className="btn btn-sm btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !url.trim()}>
            Add integration
          </button>
        </div>
      </div>
    </form>
  );
}

function Row({ projectKey, integration, onSecret }: { projectKey: string; integration: IntegrationDTO; onSecret: (s: string) => void }) {
  const update = useUpdateIntegration(projectKey);
  const remove = useDeleteIntegration(projectKey);
  const test = useTestIntegration(projectKey);
  const rotate = useRotateIntegrationSecret(projectKey);
  const toast = useToast();
  const Icon = integration.kind === 'rocketchat' ? MessageSquare : Webhook;

  return (
    <li className="list-row items-start gap-3">
      <span className="dr-tint-primary grid size-9 shrink-0 place-items-center rounded-full">
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <div className="list-col-grow min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate font-medium">{integration.urlHost}</span>
          <span className="badge badge-ghost badge-xs">{integration.kind === 'rocketchat' ? 'Rocket.Chat' : 'Webhook'}</span>
          {!integration.enabled && (
            <span className="badge badge-warning badge-soft badge-xs gap-1">
              <CircleX className="size-2.5" aria-hidden="true" />
              Off
            </span>
          )}
        </div>
        <p className="mt-0.5 text-sm text-base-content/60">
          {SEVERITY_LABEL[integration.minSeverity]}
          {integration.categories.length > 0 ? ` · ${integration.categories.map((c) => CATEGORY_META[c].label).join(', ')}` : ' · all categories'}
        </p>
        <p className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-base-content/55">
          {integration.lastDeliveryAt ? (
            <>
              <span className="inline-flex items-center gap-1">
                {integration.lastStatus && integration.lastStatus >= 200 && integration.lastStatus < 300 ? (
                  <CircleCheck className="size-3 dr-ink-success" aria-hidden="true" />
                ) : (
                  <CircleX className="size-3 dr-ink-error" aria-hidden="true" />
                )}
                Last delivery {integration.lastStatus || 'no response'}
              </span>
              <span>
                · <RelativeTime value={integration.lastDeliveryAt} />
              </span>
            </>
          ) : (
            <span>Nothing delivered yet</span>
          )}
        </p>
        {integration.disabledReason ? <p className="mt-1 text-xs dr-ink-warning">{integration.disabledReason}</p> : null}
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-1">
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          disabled={test.isPending}
          onClick={() =>
            test.mutate(integration.id, {
              onSuccess: (r) => (r.ok ? toast.success(`Receiver answered ${r.status}`) : toast.error(`Receiver answered ${r.status || 'nothing'}`, { description: r.error })),
            })
          }
        >
          <Send className="size-3.5" aria-hidden="true" />
          Test
        </button>
        {integration.hasSecret && (
          <button type="button" className="btn btn-sm btn-ghost" disabled={rotate.isPending} onClick={() => rotate.mutate(integration.id, { onSuccess: (r) => onSecret(r.secret) })}>
            Rotate secret
          </button>
        )}
        <input
          type="checkbox"
          className="toggle toggle-sm toggle-primary"
          checked={integration.enabled}
          disabled={update.isPending}
          onChange={(e) => update.mutate({ id: integration.id, enabled: e.target.checked })}
          aria-label={`${integration.urlHost} enabled`}
        />
        <button type="button" className="btn btn-square btn-sm btn-ghost" aria-label="Remove integration" disabled={remove.isPending} onClick={() => remove.mutate(integration.id)}>
          <Trash2 className="size-3.5" aria-hidden="true" />
        </button>
      </div>
    </li>
  );
}

